import { z } from 'zod'

import { CaptureRefSchema } from './capture'

// =====================
// Communication Types (formerly LeadCommunication)
// =====================
// Despite the filename-level rename, `LeadTimelineItem` and
// `LeadCommunicationStats` still carry a `Lead` prefix because they describe
// lead-specific aggregations that aren't yet generalized. They stay here until
// a follow-up factors them into module-agnostic shapes.

// Enum for communication types
export enum CommunicationTypeEnum {
  EMAIL = 'EMAIL',
  CALL = 'CALL',
  MEETING = 'MEETING',
  SMS = 'SMS',
  LINKEDIN = 'LINKEDIN',
  WHATSAPP = 'WHATSAPP',
  OTHER = 'OTHER',
}

// Enum for communication direction
export enum CommunicationDirectionEnum {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

// Enum for communication status
export enum CommunicationStatusEnum {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  OPENED = 'OPENED',
  CLICKED = 'CLICKED',
  REPLIED = 'REPLIED',
  BOUNCED = 'BOUNCED',
  FAILED = 'FAILED',
}

// Call outcome options
export enum CallOutcomeEnum {
  CONNECTED = 'CONNECTED',
  NO_ANSWER = 'NO_ANSWER',
  LEFT_VOICEMAIL = 'LEFT_VOICEMAIL',
  BUSY = 'BUSY',
  WRONG_NUMBER = 'WRONG_NUMBER',
  CALLBACK_REQUESTED = 'CALLBACK_REQUESTED',
  MEETING_BOOKED = 'MEETING_BOOKED',
}

// Display configuration for communication types
export const COMMUNICATION_TYPE_CONFIG: Record<
  CommunicationTypeEnum,
  { label: string; icon: string; color: string; bgColor: string }
> = {
  [CommunicationTypeEnum.EMAIL]: {
    label: 'Email',
    icon: 'EnvelopeSimple',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  [CommunicationTypeEnum.CALL]: {
    label: 'Call',
    icon: 'Phone',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  [CommunicationTypeEnum.MEETING]: {
    label: 'Meeting',
    icon: 'CalendarBlank',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  [CommunicationTypeEnum.SMS]: {
    label: 'SMS',
    icon: 'ChatText',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  [CommunicationTypeEnum.LINKEDIN]: {
    label: 'LinkedIn',
    icon: 'LinkedinLogo',
    color: 'text-sky-600',
    bgColor: 'bg-sky-100',
  },
  [CommunicationTypeEnum.WHATSAPP]: {
    label: 'WhatsApp',
    icon: 'WhatsappLogo',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  [CommunicationTypeEnum.OTHER]: {
    label: 'Other',
    icon: 'ChatCircle',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
}

// Display configuration for communication status
export const COMMUNICATION_STATUS_CONFIG: Record<
  CommunicationStatusEnum,
  { label: string; color: string; bgColor: string }
> = {
  [CommunicationStatusEnum.DRAFT]: {
    label: 'Draft',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
  [CommunicationStatusEnum.SCHEDULED]: {
    label: 'Scheduled',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  [CommunicationStatusEnum.SENT]: {
    label: 'Sent',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  [CommunicationStatusEnum.DELIVERED]: {
    label: 'Delivered',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  [CommunicationStatusEnum.OPENED]: {
    label: 'Opened',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  [CommunicationStatusEnum.CLICKED]: {
    label: 'Clicked',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  [CommunicationStatusEnum.REPLIED]: {
    label: 'Replied',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  [CommunicationStatusEnum.BOUNCED]: {
    label: 'Bounced',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  [CommunicationStatusEnum.FAILED]: {
    label: 'Failed',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
}

// Display configuration for call outcomes
export const CALL_OUTCOME_CONFIG: Record<
  CallOutcomeEnum,
  { label: string; color: string }
> = {
  [CallOutcomeEnum.CONNECTED]: { label: 'Connected', color: 'text-green-600' },
  [CallOutcomeEnum.NO_ANSWER]: { label: 'No Answer', color: 'text-amber-600' },
  [CallOutcomeEnum.LEFT_VOICEMAIL]: {
    label: 'Left Voicemail',
    color: 'text-blue-600',
  },
  [CallOutcomeEnum.BUSY]: { label: 'Busy', color: 'text-orange-600' },
  [CallOutcomeEnum.WRONG_NUMBER]: {
    label: 'Wrong Number',
    color: 'text-red-600',
  },
  [CallOutcomeEnum.CALLBACK_REQUESTED]: {
    label: 'Callback Requested',
    color: 'text-purple-600',
  },
  [CallOutcomeEnum.MEETING_BOOKED]: {
    label: 'Meeting Booked',
    color: 'text-blue-600',
  },
}

// Zod schemas for validation
export const CommunicationAttachmentSchema = z.object({
  id: z.string().uuid(),
  communicationId: z.string().uuid(),
  fileName: z.string(),
  fileSize: z.number(),
  mimeType: z.string(),
  storageKey: z.string(),
  url: z.string().nullable().optional(),
  externalId: z.string().nullable().optional(),
  createdAt: z.date(),
})

export type CommunicationAttachment = z.infer<
  typeof CommunicationAttachmentSchema
>

// Main Communication schema
export const CommunicationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  contactId: z.string().uuid().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),

  // Communication details
  type: z.nativeEnum(CommunicationTypeEnum),
  direction: z.nativeEnum(CommunicationDirectionEnum),
  subject: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  bodyHtml: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),

  // Email-specific fields
  externalId: z.string().nullable().optional(),
  threadId: z.string().nullable().optional(),
  messageId: z.string().nullable().optional(),
  inReplyTo: z.string().nullable().optional(),
  fromEmail: z.string().nullable().optional(),
  fromName: z.string().nullable().optional(),
  toEmails: z.array(z.string()).default([]),
  ccEmails: z.array(z.string()).default([]),
  bccEmails: z.array(z.string()).default([]),

  // Call-specific fields
  duration: z.number().nullable().optional(),
  callOutcome: z.string().nullable().optional(),
  recordingUrl: z.string().nullable().optional(),
  transcription: z.string().nullable().optional(),

  // Meeting-specific fields
  scheduledAt: z.date().nullable().optional(),
  scheduledEndAt: z.date().nullable().optional(),
  location: z.string().nullable().optional(),
  meetingUrl: z.string().nullable().optional(),
  attendees: z.any().nullable().optional(),

  // Email tracking metrics
  status: z
    .nativeEnum(CommunicationStatusEnum)
    .default(CommunicationStatusEnum.SENT),
  openCount: z.number().default(0),
  clickCount: z.number().default(0),
  firstOpenedAt: z.date().nullable().optional(),
  lastOpenedAt: z.date().nullable().optional(),
  repliedAt: z.date().nullable().optional(),
  bouncedAt: z.date().nullable().optional(),
  bounceReason: z.string().nullable().optional(),

  // Linked content
  linkedNoteId: z.string().uuid().nullable().optional(),
  linkedTaskId: z.string().uuid().nullable().optional(),

  // Metadata
  metadata: z.any().nullable().optional(),
  sentBy: z.string().uuid().nullable().optional(),
  sentAt: z.date().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),

  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion (email sync); null for manually logged communications.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
})

export type Communication = z.infer<typeof CommunicationSchema>

// Extended type with relations
export interface CommunicationWithRelations extends Communication {
  lead?: {
    id: string
    firstName: string
    lastName: string
    email: string
    company?: string
  }
  contact?: {
    id: string
    firstName: string
    lastName: string
    email?: string
  } | null
  sender?: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  } | null
  creator?: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  } | null
  attachments?: CommunicationAttachment[]
}

// Create DTO schema
export const CreateCommunicationSchema = z.object({
  contactId: z.string().uuid().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  type: z.nativeEnum(CommunicationTypeEnum),
  direction: z.nativeEnum(CommunicationDirectionEnum),
  subject: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  bodyHtml: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),

  // Email fields
  externalId: z.string().nullable().optional(),
  threadId: z.string().nullable().optional(),
  messageId: z.string().nullable().optional(),
  inReplyTo: z.string().nullable().optional(),
  fromEmail: z.string().email().nullable().optional(),
  fromName: z.string().nullable().optional(),
  toEmails: z.array(z.string().email()).optional(),
  ccEmails: z.array(z.string().email()).optional(),
  bccEmails: z.array(z.string().email()).optional(),

  // Call fields
  duration: z.number().nullable().optional(),
  callOutcome: z.nativeEnum(CallOutcomeEnum).nullable().optional(),
  recordingUrl: z.string().url().nullable().optional(),
  transcription: z.string().nullable().optional(),

  // Meeting fields
  scheduledAt: z.string().datetime().nullable().optional(),
  scheduledEndAt: z.string().datetime().nullable().optional(),
  location: z.string().nullable().optional(),
  meetingUrl: z.string().url().nullable().optional(),
  attendees: z
    .array(
      z.object({
        email: z.string().email(),
        name: z.string().optional(),
        status: z
          .enum(['PENDING', 'ACCEPTED', 'DECLINED', 'TENTATIVE'])
          .optional(),
      }),
    )
    .optional(),

  // Tracking
  status: z.nativeEnum(CommunicationStatusEnum).optional(),
  sentAt: z.string().datetime().nullable().optional(),

  // Links
  linkedNoteId: z.string().uuid().nullable().optional(),
  linkedTaskId: z.string().uuid().nullable().optional(),

  // Metadata
  metadata: z.record(z.string(), z.any()).optional(),
})

export type CreateCommunication = z.infer<typeof CreateCommunicationSchema>

// Update DTO schema
export const UpdateCommunicationSchema =
  CreateCommunicationSchema.partial().extend({
    openCount: z.number().optional(),
    clickCount: z.number().optional(),
    firstOpenedAt: z.string().datetime().nullable().optional(),
    lastOpenedAt: z.string().datetime().nullable().optional(),
    repliedAt: z.string().datetime().nullable().optional(),
    bouncedAt: z.string().datetime().nullable().optional(),
    bounceReason: z.string().nullable().optional(),
  })

export type UpdateCommunication = z.infer<typeof UpdateCommunicationSchema>

// Query params for listing communications
export const CommunicationQuerySchema = z.object({
  contactId: z.string().uuid().optional(),
  type: z.nativeEnum(CommunicationTypeEnum).optional(),
  direction: z.nativeEnum(CommunicationDirectionEnum).optional(),
  status: z.nativeEnum(CommunicationStatusEnum).optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  search: z.string().optional(),
  page: z.number().min(1).default(1),
  limit: z.number().min(1).max(100).default(20),
  sortBy: z
    .enum(['createdAt', 'sentAt', 'scheduledAt', 'type'])
    .default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

export type CommunicationQuery = z.infer<typeof CommunicationQuerySchema>

// Response schemas
export const CommunicationResponseSchema = z.object({
  data: CommunicationSchema,
  message: z.string().optional(),
})

export type CommunicationResponse = z.infer<typeof CommunicationResponseSchema>

export const CommunicationsResponseSchema = z.object({
  data: z.array(CommunicationSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  message: z.string().optional(),
})

export type CommunicationsResponse = z.infer<
  typeof CommunicationsResponseSchema
>

// Timeline item type for unified timeline view
export interface LeadTimelineItem {
  id: string
  type: 'COMMUNICATION' | 'NOTE' | 'TASK' | 'STAGE_CHANGE' | 'FIELD_CHANGE'
  timestamp: Date
  title: string
  description?: string
  metadata: Record<string, unknown>
  actor?: {
    id: string
    name: string
    email: string
    image?: string
  }
}

// Communication stats for lead dashboard
export interface LeadCommunicationStats {
  totalCommunications: number
  byType: Record<CommunicationTypeEnum, number>
  byDirection: {
    inbound: number
    outbound: number
  }
  emailStats: {
    sent: number
    opened: number
    clicked: number
    replied: number
    bounced: number
    openRate: number
    clickRate: number
    replyRate: number
  }
  callStats: {
    total: number
    connected: number
    noAnswer: number
    voicemail: number
    averageDuration: number
  }
  lastContactedAt?: Date
  daysSinceLastContact?: number
}

// Helper functions
export function getCommunicationTypeConfig(type: CommunicationTypeEnum) {
  return COMMUNICATION_TYPE_CONFIG[type]
}

export function getCommunicationStatusConfig(status: CommunicationStatusEnum) {
  return COMMUNICATION_STATUS_CONFIG[status]
}

export function getCallOutcomeConfig(outcome: CallOutcomeEnum) {
  return CALL_OUTCOME_CONFIG[outcome]
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}
